import { Canvas } from "@react-three/fiber";
import { ContactShadows } from "@react-three/drei";
import * as THREE from "three";
import { PALETTE } from "./palette";
import { CameraRig } from "./iso/CameraRig";
import { LightingRig } from "./iso/LightingRig";
import { DustMotes } from "./iso/DustMotes";
import { Board } from "./iso/Board";
import { GridPicker } from "./iso/GridPicker";
import { HoverHighlight } from "./iso/HoverHighlight";
import { PlacementGhost } from "./iso/PlacementGhost";
import { PipeRoutePreview } from "./iso/PipeRoutePreview";
import { SoldBackMarks } from "./iso/SoldBackMarks";
import { PostStack } from "./post/PostStack";
import { PerfProbe } from "./PerfProbe";
import { Rig } from "./Rig";
import { useGameStore } from "../app/gameStore";
import { useSettingsStore, type Quality } from "../app/settingsStore";

/**
 * The 2.5D isometric stage. Everything visual hangs off here; the DOM HUD
 * overlays it rather than living inside the Canvas.
 *
 * The board is sized from the snapshot, not a constant: stages are different
 * shapes, and the table has to be the one the stage is actually played on.
 */

/**
 * Device pixel ratio per quality tier, and the single biggest lever on frame
 * time in the whole renderer: every post pass runs per pixel, so halving the
 * ratio on a retina display buys more than dropping any individual effect.
 */
const DPR: Record<Quality, [number, number] | number> = {
  high: [1, 2],
  medium: [1, 1.5],
  low: 1,
};

export function Scene() {
  const cols = useGameStore((s) => s.snapshot.cols);
  const rows = useGameStore((s) => s.snapshot.rows);
  const contaminatedTiles = useGameStore((s) => s.snapshot.contaminatedTiles);
  const quality = useSettingsStore((s) => s.quality);
  const reduceMotion = useSettingsStore((s) => s.reduceMotion);

  // No tone mapping: ACES is filmic and rolls off exactly the flat color bands
  // that cel shading depends on. Keep the palette literal.
  return (
    <Canvas
      shadows
      dpr={DPR[quality]}
      gl={{ antialias: false, toneMapping: THREE.NoToneMapping }}
      style={{ position: "absolute", inset: 0 }}
    >
      <color attach="background" args={[PALETTE.creamDark]} />
      <fog attach="fog" args={[PALETTE.creamDark, 18, 34]} />

      <CameraRig />
      <LightingRig />

      <Board cols={cols} rows={rows} contaminatedTiles={contaminatedTiles} />
      <GridPicker cols={cols} rows={rows} />
      <HoverHighlight />
      <PlacementGhost />
      <PipeRoutePreview />
      <SoldBackMarks />
      <Rig />

      <ContactShadows
        position={[(cols - 1) * 0.5, -0.42, (rows - 1) * 0.5]}
        scale={Math.max(cols, rows) * 2.2}
        opacity={0.4}
        blur={2.5}
        far={4}
        color={PALETTE.chassisGreyDark}
      />

      {/* Drifting motes are atmosphere, and they are motion for its own sake —
          the first thing to go when either budget is tight. */}
      {quality !== "low" && !reduceMotion && <DustMotes />}

      <PostStack />
      <PerfProbe />
    </Canvas>
  );
}
